import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { assetsApi, merchantsApi } from 'api';
import {
  DataWrapper,
  InternalUserWallet,
  MyAssets,
  PageHeader,
  TotalBalance,
} from 'components';
import { QueryKey, UserRole } from 'enums';
import { useCalculateAssetBalances, useCurrencies } from 'hooks';
import { TranslationNamespace } from 'i18n';

export const MerchantMainPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Merchant, {
    keyPrefix: 'pages.assets.balances',
  });

  const merchantQueryResult = useQuery(QueryKey.MyInfo, merchantsApi.getMyInfo);

  const queryResult = useQuery(QueryKey.ShopAssets, assetsApi.getAllShops);
  const shopGroupQueryResult = useQuery(
    QueryKey.ShopGroupAssets,
    assetsApi.getAllShopGroups,
  );

  const mergedAssets = useMemo(
    () => [...(queryResult.data || []), ...(shopGroupQueryResult.data || [])],
    [queryResult.data, shopGroupQueryResult.data],
  );
  const { balances } = useCalculateAssetBalances(mergedAssets);

  const { defaultAssetCurrency } = useCurrencies();

  const allBalances = useMemo(
    () => [
      ...(merchantQueryResult.data?.depositAmount && defaultAssetCurrency?.id
        ? [
            {
              id: defaultAssetCurrency.id,
              amount: merchantQueryResult.data?.depositAmount,
              title: t('deposit_amount'),
            },
          ]
        : []),
      ...balances,
    ],
    [
      balances,
      defaultAssetCurrency?.id,
      merchantQueryResult.data?.depositAmount,
      t,
    ],
  );

  return (
    <Fragment>
      <PageHeader title={t('title')} />
      <Stack direction="column" spacing={8}>
        <DataWrapper
          isLoading={queryResult.isLoading || shopGroupQueryResult.isLoading}
          isEmpty={isEmpty(allBalances)}
          isError={queryResult.isError || shopGroupQueryResult.isError}
        >
          <TotalBalance balances={allBalances} />
        </DataWrapper>
        <PageHeader title={t('my_shops')} divider={false} />
        <MyAssets role={UserRole.Merchant} />
        <PageHeader title={t('my_wallets')} divider={false} />
        <InternalUserWallet />
      </Stack>
    </Fragment>
  );
};
